import { useState, type FormEvent, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { User } from 'firebase/auth';
import { useTranslation } from 'react-i18next';

import type { AuthState } from '../features/auth/useAuth';
import { StripeBand } from '@talk2nick/ui';
import { signInWithGoogle } from '../features/auth/signIn';
import { pinAutoComplete, pinInputClassName, pinInputType } from '../features/auth/pinMask';
import {
  HandleTakenError,
  handleErrorKey,
  submitOnboarding,
  useHandleAvailability,
} from '../features/auth/useOnboarding';
import {
  isOwnerEmail,
  OWNER_HANDLE,
  OWNER_HANDLE_DISPLAY,
  OWNER_HANDLE_ENABLED,
} from '@talk2nick/core';

import { Footer } from '../components/Footer';

const inputClass =
  'w-full rounded-[var(--radius-md)] border border-[var(--divider)] bg-[var(--bg-list)] px-4 py-3';
const primaryButtonClass =
  'pressable w-full rounded-[var(--radius-md)] bg-[var(--accent)] px-6 py-3 font-medium text-white disabled:opacity-40';

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
      {children}
    </div>
  );
}

/**
 * 첫 진입(온보딩) 전용 상단 Signature Stripe 밴드 (PLAN 6-2A-2).
 *
 * 위치가 고정인 이유는 두 가지다. 1) 여기만 **1회 노출**이어야 해서
 * (로그인 이후로는 다시 보이지 않는다), 2) 안전영역(--safe-top) 위에 얹혀야
 * 노치에서도 잘리지 않는다. `app-shell`이 이미 `100dvh`라 fixed가 안전하다.
 */
function OnboardingStripe() {
  return (
    <div
      className="fixed inset-x-0 top-0 z-[var(--z-appbar)]"
      style={{ paddingTop: 'var(--safe-top)' }}
    >
      <StripeBand />
    </div>
  );
}

/**
 * 진입 라우트.
 * 인증 상태에 따라 로그인 → 온보딩(유니크 ID + PIN) → 채팅 목록으로 분기한다.
 * 인증 로딩 중에는 부트 스크린을 유지해 깜빡임을 막는다.
 * PIN 잠금 화면은 라우트 앞단의 `LockGate`가 맡는다.
 */
export function HomeRoute({ auth }: { auth: AuthState }) {
  const { status, firebaseUser, profile, profileLoaded, redirectError } = auth;
  const { t } = useTranslation();

  if (status === 'loading') {
    return (
      <Centered>
        <p className="text-sm text-[var(--text-secondary)]">{t('status.loading')}</p>
      </Centered>
    );
  }

  if (status === 'unauthenticated') {
    return <LoginScreen redirectError={redirectError} />;
  }

  // 프로필 스냅샷 대기 — 잠금 해제 직후에는 Firestore가 빈 캐시로 다시 붙으므로
  // 이 구간이 잠깐 생긴다. 여기서 온보딩으로 분기하면 기존 사용자가 잘못 보인다.
  if (!profileLoaded) {
    return (
      <Centered>
        <p className="text-sm text-[var(--text-secondary)]">{t('status.loading')}</p>
      </Centered>
    );
  }

  // 인증 + 프로필 미완성(온보딩) → 유니크 ID + PIN 설정
  if (!profile && firebaseUser) {
    return <OnboardingScreen user={firebaseUser} />;
  }

  // 초대 링크를 통해 들어왔다가 온보딩을 마친 경우, 원래 티켓 경로로 복귀한다
  const pendingToken =
    typeof window !== 'undefined' ? sessionStorage.getItem('pending_join_token') : null;
  if (pendingToken) {
    sessionStorage.removeItem('pending_join_token');
    return <Navigate to={`/join/${pendingToken}`} replace />;
  }

  return <Navigate to="/chats" replace />;
}

/**
 * 미인증 진입 화면 — Google 로그인.
 * `redirectError`는 모바일 리디렉션 로그인이 돌아온 뒤 실패한 경우의 코드다.
 * 이걸 보여주지 않으면 실패가 조용히 로그인 화면으로 되돌아오는 것처럼 보여
 * 사용자는 같은 버튼을 다시 누르는 루프에 빠진다 (갤럭시 인터넷 제보).
 */
function LoginScreen({ redirectError }: { redirectError: string | null }) {
  const { t } = useTranslation();
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onGoogleSignIn() {
    setError(null);
    setSigningIn(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      console.error('[talk2nIcK] Google 로그인 실패:', err);
      setError(t('error.generic'));
    } finally {
      setSigningIn(false);
    }
  }

  return (
    <div className="flex flex-1 flex-col justify-between items-center w-full min-h-[100dvh] px-4 pt-2 pb-0">
      <div className="flex-none h-2" />
      <div className="flex flex-col items-center justify-center gap-4 text-center w-full max-w-xs my-auto">
        <div className="w-16 h-16 rounded-2xl bg-[#0b141a] p-2 flex items-center justify-center shadow-lg border border-[var(--divider)]">
          <img src="/favicon.svg" alt="talk2nIcK" className="w-12 h-12" />
        </div>
        <h1 className="text-2xl font-semibold">talk2nIcK</h1>
        <p className="text-sm text-[var(--text-secondary)]">{t('app.subtitle')}</p>
        <button
          type="button"
          className={`${primaryButtonClass} max-w-xs`}
          onClick={onGoogleSignIn}
          disabled={signingIn}
        >
          {signingIn ? t('common.loading') : t('auth.googleSignIn')}
        </button>
        {error && <p className="text-xs text-[var(--danger)]">{error}</p>}
        {redirectError && !error && (
          <p className="text-xs text-[var(--danger)]">
            {t('auth.redirectFailed', { code: redirectError })}
          </p>
        )}
        <p className="text-xs text-[var(--text-secondary)]">{t('auth.2ndPinHint')}</p>
      </div>
      <Footer />
    </div>
  );
}

/**
 * 온보딩 2단계 (PLAN 5-1장).
 * 1) 유니크 ID 입력 + 실시간 가용성 확인 (확인 시점은 예약이 아님)
 * 2) PIN 설정 — 최종 제출 시 handles 선점 transaction → users/{uid} 생성
 */
function OnboardingScreen({ user }: { user: User }) {
  const { t } = useTranslation();
  // 소유자는 전용 ID가 정해져 있으므로 선택 단계가 없다.
  const owner = isOwnerEmail(user.email);
  const [step, setStep] = useState<'id' | 'pin'>(owner ? 'pin' : 'id');
  const [handle, setHandle] = useState(owner ? OWNER_HANDLE : '');
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const availability = useHandleAvailability(handle, user.email);
  const pinValid = /^\d{6}$/.test(pin);
  const pinMatches = pin.length > 0 && pin === pinConfirm;

  function handleMessage(): string | null {
    if (error) return error;
    const { status, reason, normalized } = availability;
    if (status === 'invalid') return reason ? t(handleErrorKey(reason)) : null;
    if (status === 'checking') return t('onboarding.checking');
    if (status === 'available') return t('onboarding.available', { handle: normalized });
    if (status === 'taken') return t('onboarding.taken');
    if (status === 'error') return t('error.generic');
    return null;
  }

  function onContinueFromId(e: FormEvent) {
    e.preventDefault();
    if (availability.status !== 'available') return;
    setError(null);
    setStep('pin');
  }

  async function onFinish(e: FormEvent) {
    e.preventDefault();
    if (!pinValid || !pinMatches) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitOnboarding(user, handle, pin);
      // 성공하면 useAuth의 users/{uid} 구독이 갱신되어 /chats로 이동한다.
    } catch (err) {
      console.error('[talk2nIcK] 온보딩 실패:', err);
      if (err instanceof HandleTakenError) {
        // 확인과 제출 사이에 다른 사람이 선점한 경우 → 1단계로 되돌린다
        setStep('id');
        setError(t('onboarding.taken'));
      } else {
        setError(t('error.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (step === 'pin') {
    const pinError =
      pin.length > 0 && !pinValid
        ? t('onboarding.pinLength')
        : pinConfirm.length > 0 && !pinMatches
          ? t('onboarding.pinMismatch')
          : null;

    return (
      <Centered>
        <OnboardingStripe />
        <h1 className="text-lg font-semibold">{t('onboarding.pinTitle')}</h1>
        <p className="text-sm text-[var(--text-secondary)]">{t('onboarding.pinHint')}</p>
        <p className="rounded-[var(--radius-md)] bg-[var(--bg-list)] px-4 py-2 text-sm font-medium">
          @{owner && OWNER_HANDLE_ENABLED ? OWNER_HANDLE_DISPLAY : availability.normalized}
        </p>
        <form onSubmit={onFinish} className="w-full max-w-xs space-y-3">
          {/* 마스킹 방식은 pinMask가 정한다. pattern은 inputMode를 무시하는 일부
              안드로이드 키보드에서도 숫자 키패드를 띄우기 위한 관용구다. */}
          <input
            type={pinInputType}
            className={pinInputClassName(`${inputClass} text-center text-2xl tracking-widest`)}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder={t('onboarding.pinPlaceholder')}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={pinAutoComplete}
            maxLength={6}
            autoFocus
            disabled={submitting}
          />
          <input
            type={pinInputType}
            className={pinInputClassName(`${inputClass} text-center text-2xl tracking-widest`)}
            value={pinConfirm}
            onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder={t('onboarding.pinConfirmPlaceholder')}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={pinAutoComplete}
            maxLength={6}
            disabled={submitting}
          />
          {(pinError ?? error) && (
            <p className="text-xs text-[var(--danger)]">{pinError ?? error}</p>
          )}
          <button
            type="submit"
            className={primaryButtonClass}
            disabled={!pinValid || !pinMatches || submitting}
          >
            {submitting ? t('onboarding.finishing') : t('onboarding.finish')}
          </button>
          <button
            type="button"
            className="tap-target w-full text-xs text-[var(--text-secondary)]"
            disabled={submitting}
            onClick={() => {
              setError(null);
              setStep('id');
            }}
          >
            {t('common.back')}
          </button>
        </form>
      </Centered>
    );
  }

  const message = handleMessage();
  const messageClass =
    availability.status === 'available' ? 'text-[var(--accent)]' : 'text-[var(--danger)]';

  return (
    <Centered>
      <OnboardingStripe />
      <h1 className="text-lg font-semibold">{t('onboarding.title')}</h1>
      <p className="text-sm text-[var(--text-secondary)]">{t('onboarding.hint')}</p>
      <form onSubmit={onContinueFromId} className="w-full max-w-xs space-y-3">
        <input
          className={inputClass}
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          placeholder={t('onboarding.placeholder')}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
          disabled={submitting}
          aria-invalid={availability.status === 'invalid' || availability.status === 'taken'}
        />
        {message && <p className={`text-xs ${messageClass}`}>{message}</p>}
        <button
          type="submit"
          className={primaryButtonClass}
          disabled={availability.status !== 'available' || submitting}
        >
          {t('common.continue')}
        </button>
      </form>
    </Centered>
  );
}
